import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';

import { cleanEntity } from '../shared/util/entity-utils/entity-utils';
import { createEntitySlice, EntityState, getAllRequest, serializeAxiosError } from '../shared/reducers/reducer.utils';


const initialState: EntityState<any> = {
    loading: false,
    errorMessage: null,
    entities: [],
    entity: {},
    updating: false,
    totalItems: 0,
    updateSuccess: false,
};

const apiUrl = 'api/contactos';

// Actions

export const getEntities = createAsyncThunk(
    'contacto/fetch_entity_list',
    async (request: any) => {
        const params = getAllRequest(request);
        const requestUrl = `${apiUrl}?${params}&cacheBuster=${new Date().getTime()}`;
        return axios.get<any[]>(requestUrl);
    },
    { serializeError: serializeAxiosError },
);

export const getEntity = createAsyncThunk(
    'contacto/fetch_entity',
    async (id: string | number) => {
        const requestUrl = `${apiUrl}/${id}`;
        return axios.get<any>(requestUrl);
    },
    { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
    'contacto/create_entity',
    async (entity: any, thunkAPI) => {
        const result = await axios.post<any>(apiUrl, cleanEntity(entity));
        thunkAPI.dispatch(getEntities({}));
        return result;
    },
    { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
    'contacto/update_entity',
    async (entity: any, thunkAPI) => {
        const result = await axios.put<any>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
        thunkAPI.dispatch(getEntities({}));
        return result;
    },
    { serializeError: serializeAxiosError },
);

export const partialUpdateEntity = createAsyncThunk(
    'contacto/partial_update_entity',
    async (entity: any, thunkAPI) => {
        const result = await axios.patch<any>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
        thunkAPI.dispatch(getEntities({}));
        return result;
    },
    { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
    'contacto/delete_entity',
    async (id: string | number, thunkAPI) => {
        const requestUrl = `${apiUrl}/${id}`;
        const result = await axios.delete<any>(requestUrl);
        thunkAPI.dispatch(getEntities({}));
        return result;
    },
    { serializeError: serializeAxiosError },
);

// slice

export const ContactoSlice = createEntitySlice({
    name: 'contacto',
    initialState,
    extraReducers(builder) {
        builder
            .addCase(getEntity.fulfilled, (state, action) => {
                state.loading = false;
                state.entity = action.payload.data;
            })
            .addCase(deleteEntity.fulfilled, state => {
                state.updating = false;
                state.updateSuccess = true;
                state.entity = {};
            })
            .addMatcher(isFulfilled(getEntities), (state, action) => {
                const { data, headers } = action.payload;

                return {
                    ...state,
                    loading: false,
                    entities: data,
                    totalItems: parseInt(headers['x-total-count'], 10),
                };
            })
            .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
                state.updating = false;
                state.loading = false;
                state.updateSuccess = true;
                state.entity = action.payload.data;
            })
            .addMatcher(isPending(getEntities, getEntity), state => {
                state.errorMessage = null;
                state.updateSuccess = false;
                state.loading = true;
            })
            .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
                state.errorMessage = null;
                state.updateSuccess = false;
                state.updating = true;
            });
    },
});

export const { reset } = ContactoSlice.actions;

// Reducer
export default ContactoSlice.reducer;
